import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleNavClick = (e, targetId) => {
        e.preventDefault();
        setIsOpen(false);

        if (location.pathname !== '/') {
            navigate('/', { state: { target: targetId } });
        } else {
            const element = document.getElementById(targetId);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    };

    const handleContactClick = () => {
        setIsOpen(false);
        const element = document.getElementById('contacts');
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        } else {
            // Fallback if footer not found (shouldn't happen) or if we want to ensure we are on a page with footer
        }
    };

    return (
        <nav
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled
                ? 'bg-black/80 backdrop-blur-md border-b border-white/10 py-4'
                : 'bg-transparent py-6'
                }`}
        >
            <div className="container mx-auto px-6 flex justify-between items-center">
                <Link to="/" className="text-2xl font-bold tracking-tighter text-white uppercase mix-blend-difference">
                    Ader Srl
                </Link>

                {/* Desktop Menu */}
                <div className="hidden md:flex items-center space-x-8">
                    <a href="#about" onClick={(e) => handleNavClick(e, 'about')} className="text-sm font-medium text-gray-300 hover:text-white transition-colors uppercase tracking-widest">Chi Siamo</a>
                    <a href="#services" onClick={(e) => handleNavClick(e, 'services')} className="text-sm font-medium text-gray-300 hover:text-white transition-colors uppercase tracking-widest">Servizi</a>
                    <button onClick={handleContactClick} className="bg-white text-black px-6 py-2 rounded-full font-medium hover:bg-gray-200 transition-colors">
                        Contattaci
                    </button>
                </div>

                {/* Mobile Menu Button */}
                <div className="md:hidden">
                    <button onClick={() => setIsOpen(!isOpen)} className="text-white">
                        {isOpen ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl flex flex-col justify-center items-center md:hidden"
                    >
                        {/* Background Technical Grid (Optional decorative) */}
                        <div className="absolute inset-0 z-0 opacity-10 pointer-events-none"
                            style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '40px 40px' }}
                        />

                        {/* Explicit Close Button inside Overlay */}
                        <button
                            onClick={() => setIsOpen(false)}
                            className="absolute top-6 right-6 text-white p-2 z-50 hover:bg-white/10 rounded-full transition-colors"
                        >
                            <X size={32} />
                        </button>

                        <motion.div
                            className="flex flex-col items-center space-y-8 z-10 w-full px-8"
                            initial="closed"
                            animate="open"
                            exit="closed"
                            variants={{
                                open: { transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
                                closed: { transition: { staggerChildren: 0.05, staggerDirection: -1 } }
                            }}
                        >
                            {/* 01. Chi Siamo */}
                            <motion.a
                                href="#about"
                                onClick={(e) => handleNavClick(e, 'about')}
                                variants={{
                                    open: { y: 0, opacity: 1 },
                                    closed: { y: 50, opacity: 0 }
                                }}
                                className="group relative text-center"
                            >
                                <span className="absolute -left-6 top-1 text-[10px] font-mono text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">01</span>
                                <span className="text-3xl font-bold text-white uppercase tracking-tighter hover:text-gray-400 transition-colors">
                                    Chi Siamo
                                </span>
                            </motion.a>

                            {/* 02. Servizi */}
                            <motion.a
                                href="#services"
                                onClick={(e) => handleNavClick(e, 'services')}
                                variants={{
                                    open: { y: 0, opacity: 1 },
                                    closed: { y: 50, opacity: 0 }
                                }}
                                className="group relative text-center"
                            >
                                <span className="absolute -left-6 top-1 text-[10px] font-mono text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity">02</span>
                                <span className="text-3xl font-bold text-white uppercase tracking-tighter hover:text-gray-400 transition-colors">
                                    Servizi
                                </span>
                            </motion.a>

                            {/* Divider */}
                            <motion.div
                                variants={{ open: { width: "40px" }, closed: { width: "0px" } }}
                                className="h-[1px] bg-white/20"
                            />

                            {/* CTA */}
                            <motion.button
                                onClick={handleContactClick}
                                variants={{
                                    open: { scale: 1, opacity: 1 },
                                    closed: { scale: 0.9, opacity: 0 }
                                }}
                                className="bg-white text-black px-10 py-3 rounded-full text-lg font-bold uppercase tracking-widest hover:bg-gray-200 transition-transform active:scale-95"
                            >
                                Contattaci
                            </motion.button>
                        </motion.div>

                        {/* Footer Info inside Menu */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1, transition: { delay: 0.5 } }}
                            className="absolute bottom-12 text-gray-600 text-xs font-mono"
                        >
                            ADER SYSTEM V.1.0
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default Navbar;
